import { FormControl, Grid } from '@material-ui/core';
import { Button, Card, Checkbox, FormControlLabel, Typography } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import { Discipline } from '../../models/models';
import { ComponentChecklist } from '../../rxdb/componentChecklist/queryBuilder';
import useComponentChecklist from '../../rxdb/componentChecklist/useComponentChecklist';
import { ComponentType } from '../../rxdb/componentType/queryBuilder';
import useComponentType from '../../rxdb/componentType/useComponentType';
import useAccessOnTask from '../../rxdb/taskList/useAccessOnTask';
import { useTaskList } from '../../rxdb/taskList/useTaskList';
import { sentryCaptureException, useNotification } from '../../utilities';
import { FALSE, TRUE } from '../../utilities/constants';
import { TaskStatus } from '../../utilities/enums';
import { isTaskStatusFIorLISign } from '../../utilities/utilityFunctions';
import useStyles from './Checklist.styles';

const Checklist: React.FC = () => {
  const classes = useStyles();
  const { SaveChecklist, configCatFlag, CurrentUserSelected, SiteDiscipline } = useMst();
  const { isEditable, taskData } = useAccessOnTask();
  const { successNotification } = useNotification();
  const { setTaskListInfo } = useTaskList();
  const { componentTypeListData } = useComponentType(SiteDiscipline.disciplineId);
  const { checklistData: checklistTransactionData, saveComponentChecklist } =
    useComponentChecklist();

  const [componentCheckListPayload, setComponentChecklistPayload] = useState<ComponentChecklist[]>(
    [],
  );
  const [isSaving, setIsSaving] = useState(false);

  const isShaft = SiteDiscipline.disciplineId === Discipline.Shaft;

  const checkIfAllChecked = () => {
    let checkReturn = true;
    if (componentCheckListPayload !== undefined && componentCheckListPayload?.length > 0) {
      componentCheckListPayload.forEach((val: ComponentChecklist) => {
        checkReturn = (val.checked as boolean) && checkReturn;
      });
    } else {
      return false;
    }
    return checkReturn;
  };

  const [ifAllCheckedFlag, setIfAllCheckedFlag] = useState(checkIfAllChecked());

  const checkIfInitial = (): boolean => {
    let isAnyOneChanged = false;
    componentCheckListPayload.forEach((val: ComponentChecklist) => {
      isAnyOneChanged = val.isChanged || isAnyOneChanged;
    });
    return isAnyOneChanged;
  };

  const selectAllChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIfAllCheckedFlag(e.target.checked);
    if (componentCheckListPayload !== undefined && componentCheckListPayload?.length > 0) {
      componentCheckListPayload.forEach((val: ComponentChecklist, index: number) => {
        const tempArray: ComponentChecklist[] = componentCheckListPayload;
        if (tempArray[index]?.pk_uuid) {
          tempArray[index] = {
            ...tempArray[index],
            isDelete: e.target.checked ? FALSE : TRUE,
          };
        }
        tempArray[index] = {
          ...tempArray[index],
          isChanged:
            e.target.checked === tempArray[index].checked
              ? tempArray[index].isChanged
              : !tempArray[index].isChanged,
          checked: e.target.checked,
        };
        setComponentChecklistPayload(tempArray);
      });
    }
    SaveChecklist.setIsChanged(checkIfInitial());
    SaveChecklist.setDiscardData(componentCheckListPayload);
  };

  const CheckboxField = (props: { index: number; value: boolean; label: string }) => {
    const { index, value, label } = props;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const tempArray = componentCheckListPayload;
      if (tempArray[index]?.pk_uuid) {
        tempArray[index] = {
          ...tempArray[index],
          isDelete: event.target.checked ? FALSE : TRUE,
        };
      }
      tempArray[index] = {
        ...tempArray[index],
        isChanged: !tempArray[index].isChanged,
        checked: event.target.checked,
      };
      setComponentChecklistPayload(tempArray);
      SaveChecklist.setIsChanged(checkIfInitial());
      SaveChecklist.setDiscardData(componentCheckListPayload);
      setIfAllCheckedFlag(checkIfAllChecked());
    };

    return (
      <FormControlLabel
        control={
          <Checkbox
            data-testid={`checklist-checkbox${index}`}
            disabled={!isEditable}
            defaultChecked={value}
            onChange={e => handleChange(e)}
          />
        }
        label={label}
      />
    );
  };

  const saveData = async () => {
    try {
      setIsSaving(true);
      if (isTaskStatusFIorLISign(taskData) || CurrentUserSelected.isAdhoc) {
        await setTaskListInfo(TaskStatus.IN_PROGRESS);
      }
      componentCheckListPayload.map(async (record: ComponentChecklist) => {
        if (record?.pk_uuid && record.isChanged) {
          await saveComponentChecklist({
            ...record,
            isChanged: undefined,
            fkInspectorLead: taskData?.fkInspectorLead,
            fkInspectorField: taskData?.fkInspectorField,
          });
        } else if (record.checked && record.isChanged) {
          await saveComponentChecklist({
            ...record,
            isDelete: FALSE,
            isChanged: undefined,
            fkInspectorLead: taskData?.fkInspectorLead,
            fkInspectorField: taskData?.fkInspectorField,
          });
        }
      });
      successNotification('Component Checklist Saved Successfully');
      setIsSaving(false);
      SaveChecklist.setIsChanged(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('🚀 ~ file: Checklist.tsx ~ line 250 ~ saveData ~ err', error);
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, `~ file: Checklist.tsx ~ line 250 ~ saveData ${error}`);
    }
  };

  useEffect(() => {
    setIfAllCheckedFlag(checkIfAllChecked());
  }, [componentCheckListPayload]);

  useEffect(() => {
    if (!SaveChecklist.isChangedTrue) {
      const data = componentTypeListData?.map((componentTypeObj: ComponentType) => {
        const transactionObj = checklistTransactionData.find(
          obj =>
            String(isShaft ? obj?.fk_Component : obj?.assetCategoryComponent) ===
            componentTypeObj?.id,
        );
        return {
          checkListComponentName: componentTypeObj.name,
          assetCategoryComponent: isShaft ? 0 : Number(componentTypeObj.id),
          fk_Component: isShaft ? Number(componentTypeObj.id) : 0,
          sort: componentTypeObj.sort,
          isChanged: false,
          pk_uuid: transactionObj ? transactionObj.pk_uuid : '',
          checked: transactionObj ? !(transactionObj.isDelete === TRUE) : false,
          isDelete: transactionObj ? transactionObj.isDelete : FALSE,
          pk_AssetInspectionComponentChecklist: transactionObj
            ? transactionObj.pk_AssetInspectionComponentChecklist
            : '',
        };
      });
      setComponentChecklistPayload(data);
    }
  }, [componentTypeListData, checklistTransactionData]);

  return (
    <Grid
      tabIndex={1}
      className={
        configCatFlag.getBlueBannerDisplay === true || configCatFlag.getRedBannerDisplay === true
          ? classes.root1
          : classes.root
      }
    >
      <Card elevation={1} className={classes.card}>
        <Grid container justifyContent="space-between">
          <Typography className="heading">Component Checklist</Typography>
          {isEditable && componentCheckListPayload?.length > 0 && (
            <Grid className={classes.checklistSaveButtonGrid}>
              <Button
                data-testid="checklist-save-button"
                disabled={!SaveChecklist.isChangedTrue}
                noMinHeight
                color="primary"
                variant="contained"
                onClick={saveData}
                isLoading={isSaving}
                id={
                  !SaveChecklist.isChangedTrue
                    ? 'checklistSaveButtonIsNotChanged'
                    : 'checklistSaveButtonIsChanged'
                }
              >
                <Grid id="checklistSaveButtonInnerGrid">Save</Grid>
              </Button>
            </Grid>
          )}
        </Grid>

        {isEditable && componentCheckListPayload?.length > 0 && (
          <Card elevation={1} className="selectAllBlock">
            <FormControlLabel
              control={
                <Checkbox
                  data-testid="checklist-select-all"
                  checked={ifAllCheckedFlag}
                  onChange={e => {
                    selectAllChanged(e);
                  }}
                />
              }
              label="Select All"
            />
          </Card>
        )}

        {componentCheckListPayload?.length > 0 && (
          <Card elevation={1} className="outerGrid">
            <FormControl className={classes.formControl}>
              {componentCheckListPayload
                ?.sort(
                  (a: ComponentChecklist, b: ComponentChecklist) =>
                    (a?.sort as number) - (b?.sort as number),
                )
                .map((val: ComponentChecklist, index: number) => {
                  return (
                    <>
                      <Grid
                        item
                        xs={4}
                        key={isShaft ? val.fk_Component : val.assetCategoryComponent}
                        className={classes.checkboxFieldGrid}
                      >
                        <CheckboxField
                          index={index}
                          label={val.checkListComponentName}
                          value={val.checked as boolean}
                        />
                      </Grid>
                    </>
                  );
                })}
            </FormControl>
          </Card>
        )}
      </Card>
    </Grid>
  );
};

export default observer(Checklist);
